<script setup lang="ts">
import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';

import { useEvents } from '~/composables/events/useEvents';
import {
	CondoTypeEnum,
	condoTypesTranslations
} from '~/core/entities/CRMCondoEnums';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { CACHE_TIME } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { CONDO_DETAILS } from '~~/utils/symbols';

import { condoTypeByPropertyType } from '..';

const condominium = inject(CONDO_DETAILS);
const sectionName = 'properties in the neighborhood';
const { trackCtaViewMoreClicked } = useEvents();

const key = computed(() => {
	const normalizedRegion = normalizeString(condominium?.value?.condo.region);

	return `condominium:region-properties-${normalizedRegion}`;
});

const condoType = computed(() => {
	return (
		condominium?.value?.condo.condoType.identifier || CondoTypeEnum.VERTICAL
	);
});

const query = computed<
	Pick<
		PaginatedPropertyFilters,
		| 'regions'
		| 'propertyTypes'
		| 'cities'
		| 'states'
		| 'minAskingPrice'
		| 'maxAskingPrice'
	>
>(() => {
	const minAskingPrice = condominium?.value?.propertyStatistics?.priceRange.min;
	const maxAskingPrice = condominium?.value?.propertyStatistics?.priceRange.max;

	return {
		regions: [condominium?.value?.condo.region || ''],
		cities: [condominium?.value?.condo.city || ''],
		states: [condominium?.value?.condo.state || ''],
		propertyTypes: condoTypeByPropertyType[condoType.value],
		...(minAskingPrice && { minAskingPrice }),
		...(maxAskingPrice && { maxAskingPrice })
	};
});

const defaultRegionRelatedProperties = { payload: [] };
const { user } = useUserSession();

const { data: properties, status: status } = useAsyncData(
	key.value,
	async () => {
		if (!condominium?.value?.condo.region)
			return defaultRegionRelatedProperties;

		try {
			const [result, error] = await nuxtPropertiesRepository.list({
				page: 1,
				perPage: 12,
				sortBy: 'asking_price',
				orderBy: 'desc',
				...query.value,
				markUserWishlistsId: user.value?.id
			});

			if (error) {
				return defaultRegionRelatedProperties;
			}

			return {
				payload: result.properties
			};
		} catch {
			return defaultRegionRelatedProperties;
		}
	},
	{
		default: () => defaultRegionRelatedProperties,
		lazy: true,
		...getCacheAsyncDataConfig<{ payload: Property[] }>(CACHE_TIME),
		watch: [() => condominium?.value?.condo]
	}
);

const title = computed(() => {
	const type = condoTypesTranslations[condoType.value];

	return `${type} em ${condominium?.value?.condo.region}`;
});

const seeAllHref = computed(() => {
	return PropertyFiltersPresenter.getPathFromFilters(
		query.value,
		{},
		{
			page: 'imoveis'
		}
	);
});

const isEmpty = computed(() => {
	return properties.value.payload.length === 0;
});

function handleViewMoreClicked() {
	trackCtaViewMoreClicked({
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.DESTINATION_URL]: seeAllHref.value,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Condomínio'
	});
}
</script>

<template>
	<StandardSlider
		v-if="!isEmpty"
		:items="properties.payload as Property[]"
		:view-all-href="seeAllHref"
		:title="title"
		:is-loading="status === 'pending'"
		:section-name="sectionName"
		button-variant="secondary"
		title-as="h3"
		@cta-view-more-clicked="handleViewMoreClicked"
	/>
</template>
